import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  inviteParticipant,
  listParticipants,
  removeParticipant,
  updateParticipant,
  type InviteParticipantInput,
} from '../../shared/api/participants'
import type { ParticipantRole } from '../../shared/api/types'

export const participantKeys = {
  byTrip: (tripId: string) => ['participants', tripId] as const,
}

export function useParticipants(tripId: string | undefined) {
  return useQuery({
    queryKey: participantKeys.byTrip(tripId ?? 'unknown'),
    queryFn: () => listParticipants(tripId as string),
    enabled: Boolean(tripId),
  })
}

export function useInviteParticipant(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: InviteParticipantInput) => inviteParticipant(tripId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: participantKeys.byTrip(tripId) }),
  })
}

export function useUpdateParticipant(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: ParticipantRole }) =>
      updateParticipant(tripId, userId, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: participantKeys.byTrip(tripId) }),
  })
}

export function useRemoveParticipant(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => removeParticipant(tripId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: participantKeys.byTrip(tripId) }),
  })
}
